import React from 'react';
import { Users, UserCheck, UserPlus, MapPin, Sprout, Award, AlertTriangle, TrendingDown } from 'lucide-react';

// Founder Executive Suite · web Customers tab. User demographics (country / skill /
// region), the At-Risk churn pulse (cohorts + region strategy) and a high-XP grower
// leaderboard. Mirrors the mobile CustomersTab.
const C = {
  forest: '#1A4331', forestDeep: '#0F2D1F', cream: '#FAF6EC', creamWarm: '#F4EEDE',
  lime: '#B4D330', gold: '#C9A85B', leaf: '#6B9F4A', leafDeep: '#4E7A33', terracotta: '#C66B4A',
  text: '#2D241E', textMuted: '#5C665C', textSoft: '#7A6B57', white: '#FFFFFF', sageDeep: '#D9E1CB',
  border: 'rgba(26,67,49,0.10)', leafTint: 'rgba(107,159,74,0.14)', goldTint: 'rgba(201,168,91,0.14)',
};
const OF = { fontFamily: 'Outfit, sans-serif' };
const cardCream = { background: C.cream, borderRadius: 20, border: `1px solid ${C.border}`, padding: 24, marginBottom: 20 };
const label = { fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: C.textMuted, textTransform: 'uppercase' };

const COHORTS = [
  { key: 'healthy', label: 'Healthy', color: C.leafDeep, hint: '≤3 days' },
  { key: 'watch', label: 'Watch', color: C.gold, hint: '4–6 days' },
  { key: 'at_risk', label: 'At risk', color: '#C98A2B', hint: '7–20 days' },
  { key: 'critical', label: 'Critical', color: C.terracotta, hint: '21+ days' },
];

function BarRow({ lbl, count, pct, color }) {
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 13, color: C.text }}>{lbl}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.textMuted }}>{count} · {pct}%</span>
      </div>
      <div style={{ height: 8, borderRadius: 4, background: C.sageDeep, overflow: 'hidden' }}>
        <div style={{ height: 8, borderRadius: 4, background: color, width: `${Math.max(pct, 3)}%`, transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );
}

function DemoBlock({ title, Icon, rows, colorFn }) {
  return (
    <div style={{ flex: 1, minWidth: 240 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
        <Icon className="w-4 h-4" style={{ color: C.forest }} />
        <span style={label}>{title}</span>
      </div>
      {(rows || []).length === 0 ? <p style={{ fontSize: 13, color: C.textMuted }}>No data yet.</p> :
        rows.map((r, i) => <BarRow key={i} lbl={r.label || r.region} count={r.count} pct={r.pct} color={colorFn(i)} />)}
    </div>
  );
}

const TIER_TONE = { pro: C.forest, premium: C.leafDeep, standard: C.gold, free: C.textMuted };

export default function CustomersTab({ data }) {
  if (!data) return null;
  const s = data.summary || {};
  const demo = data.demographics || {};
  const churn = data.churn_pulse || {};
  const cohorts = churn.cohorts || {};
  const board = data.leaderboard || [];
  const palette = [C.forest, C.leaf, C.gold, C.leafDeep, C.terracotta, C.textSoft];

  return (
    <div data-testid="customers-tab">
      {/* Summary tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <Tile Icon={Users} tone={C.forest} value={`${s.total_users || 0}`} label="Total growers" />
        <Tile Icon={UserCheck} tone={C.leafDeep} value={`${s.members || 0}`} label={`Members · ${s.member_pct || 0}%`} />
        <Tile Icon={Users} tone={C.textSoft} value={`${s.free || 0}`} label="Free tier" />
        <Tile Icon={UserPlus} tone={C.gold} value={`+${s.new_30d || 0}`} label="New · 30 days" />
      </div>

      {/* Demographics */}
      <div style={cardCream} data-testid="customers-demographics">
        <p style={{ ...label, marginBottom: 12 }}>Demographics</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32 }}>
          <DemoBlock title="By country" Icon={MapPin} rows={demo.by_country} colorFn={(i) => palette[i % palette.length]} />
          <DemoBlock title="By skill level" Icon={Sprout} rows={demo.by_skill} colorFn={(i) => palette[i % palette.length]} />
          <DemoBlock title="Top regions" Icon={MapPin} rows={demo.by_region} colorFn={(i) => palette[i % palette.length]} />
        </div>
      </div>

      {/* At-Risk churn pulse */}
      <div style={{ background: C.forestDeep, borderRadius: 20, border: '1px solid rgba(198,107,74,0.3)', padding: 24, marginBottom: 20 }} data-testid="customers-churn-pulse">
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <TrendingDown className="w-4 h-4" style={{ color: C.terracotta }} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: C.terracotta }}>AT-RISK CHURN PULSE</span>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          {COHORTS.map((co) => (
            <div key={co.key} style={{ flex: 1, background: 'rgba(250,246,236,0.06)', borderRadius: 12, padding: 12, textAlign: 'center' }} data-testid={`cohort-${co.key}`}>
              <p style={{ ...OF, fontSize: 26, fontWeight: 700, color: co.color, margin: 0 }}>{cohorts[co.key] || 0}</p>
              <p style={{ fontSize: 12, color: C.cream, margin: '2px 0 0' }}>{co.label}</p>
              <p style={{ fontSize: 10, color: 'rgba(250,246,236,0.5)', margin: 0 }}>{co.hint}</p>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 14, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'rgba(250,246,236,0.7)' }}>{churn.engaged_users || 0} engaged · {churn.avg_opens_30d || 0} avg opens/30d · {churn.declining_pct || 0}% opening less</span>
        </div>
        {(churn.at_risk_by_region || []).length > 0 && (
          <div style={{ marginTop: 14, borderTop: '1px solid rgba(250,246,236,0.12)', paddingTop: 14 }}>
            <p style={{ fontSize: 11, color: 'rgba(250,246,236,0.55)', margin: '0 0 6px' }}>AT-RISK BY REGION</p>
            {churn.at_risk_by_region.map((r, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                <span style={{ fontSize: 13, color: C.cream }}>{r.region}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.gold }}>{r.count} · {r.pct}%</span>
              </div>
            ))}
          </div>
        )}
        {churn.strategy && (
          <div style={{ display: 'flex', gap: 8, marginTop: 14, background: 'rgba(180,211,48,0.10)', borderRadius: 12, padding: 14 }}>
            <AlertTriangle className="w-4 h-4 shrink-0" style={{ color: C.lime, marginTop: 2 }} />
            <span style={{ fontSize: 13, color: C.cream, lineHeight: 1.5 }}>{churn.strategy}</span>
          </div>
        )}
      </div>

      {/* High-XP leaderboard */}
      <p style={{ ...label, marginBottom: 10 }}>Top growers · XP leaderboard</p>
      <div style={{ background: C.cream, borderRadius: 20, border: `1px solid ${C.border}`, overflow: 'hidden' }} data-testid="customers-leaderboard">
        {board.length === 0 ? <div style={{ padding: 32, textAlign: 'center', color: C.textMuted }}>No grower activity yet.</div> :
          board.map((m, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px', borderTop: i > 0 ? `1px solid ${C.border}` : 'none' }} data-testid={`leaderboard-row-${i}`}>
              <div style={{ width: 30, height: 30, borderRadius: 15, display: 'grid', placeItems: 'center', background: i < 3 ? C.gold : 'rgba(26,67,49,0.06)' }}>
                <span style={{ ...OF, fontSize: 14, fontWeight: 700, color: i < 3 ? C.forestDeep : C.forest }}>{m.rank}</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 14, fontWeight: 600, color: C.text, margin: 0 }}>{m.name}</p>
                <p style={{ fontSize: 12, color: C.textMuted, margin: 0 }}>{m.xp} XP · {m.streak}d streak{m.location ? ` · ${m.location}` : ''}</p>
              </div>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: TIER_TONE[m.tier] || C.textMuted, background: 'rgba(26,67,49,0.05)', borderRadius: 999, padding: '4px 10px' }}>
                <Award className="w-3 h-3" />{m.tier}
              </span>
            </div>
          ))}
      </div>
    </div>
  );
}

function Tile({ Icon, tone, value, label: lbl }) {
  return (
    <div style={{ background: C.cream, borderRadius: 14, border: `1px solid ${C.border}`, padding: 16 }}>
      <Icon className="w-4 h-4" style={{ color: tone }} />
      <p style={{ ...OF, fontSize: 26, fontWeight: 700, color: tone, margin: '4px 0 0' }}>{value}</p>
      <p style={{ fontSize: 12, color: C.textSoft, margin: 0 }}>{lbl}</p>
    </div>
  );
}
